import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ErrorText,
  Field,
  Input,
  Modal,
  Pagination,
  Select,
  formatSum,
} from '@/components/ui';

interface CustomerRow {
  id: string;
  telegramId: string | null;
  phone: string | null;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  isBlocked: boolean;
  blockedReason: string | null;
  myidVerified: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  ordersCount: number;
  openOrders: number;
  totalSpent: number;
}

interface CustomerDetail extends CustomerRow {
  photoUrl: string | null;
  locale: string;
  myidVerifiedAt: string | null;
  addresses: Array<{
    id: string;
    label: string | null;
    addressLine: string;
    distanceKm: number | null;
    isDefault: boolean;
  }>;
  stats: {
    ordersCount: number;
    openOrders: number;
    completed: number;
    cancelled: number;
    totalSpent: number;
  };
  orders: Array<{
    id: string;
    number: string;
    status: string;
    total: number;
    minStartDate: string;
    maxEndDate: string;
    createdAt: string;
  }>;
}

const fullName = (c: { firstName: string | null; lastName: string | null }) =>
  [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Без имени';

export default function CustomersPage() {
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<'all' | 'blocked' | 'verified'>('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [openId, setOpenId] = useState<string | null>(null);
  const [blocking, setBlocking] = useState<CustomerRow | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['customers', q, filter, page, limit],
    queryFn: async () =>
      (
        await api.get<{ items: CustomerRow[]; total: number; pages: number }>('/admin/customers', {
          params: {
            q: q || undefined,
            blocked: filter === 'blocked' ? true : undefined,
            verified: filter === 'verified' ? true : undefined,
            page,
            limit,
          },
        })
      ).data,
    placeholderData: keepPreviousData,
  });

  const detail = useQuery({
    queryKey: ['customer', openId],
    queryFn: async () => (await api.get<CustomerDetail>(`/admin/customers/${openId}`)).data,
    enabled: Boolean(openId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['customers'] });
    void queryClient.invalidateQueries({ queryKey: ['customer'] });
  };

  const block = useMutation({
    mutationFn: (value: { id: string; blocked: boolean; reason?: string }) =>
      value.blocked
        ? api.post(`/admin/customers/${value.id}/block`, { reason: value.reason })
        : api.post(`/admin/customers/${value.id}/unblock`),
    onSuccess: () => {
      setBlocking(null);
      setReason('');
      setError(null);
      refresh();
    },
    onError: (e) => {
      setBlocking(null);
      setError(errorMessage(e));
    },
  });

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Клиенты</h1>
        <p className="mt-1 text-sm text-neutral-500">
          {list.data ? `Всего ${list.data.total}` : 'Загрузка…'}
        </p>
      </header>

      {error && <ErrorText>{error}</ErrorText>}

      <div className="flex flex-wrap gap-3">
        <Input
          placeholder="Телефон, имя, username или Telegram id"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="max-w-sm"
        />
        <Select
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value as typeof filter);
            setPage(1);
          }}
          className="max-w-52"
        >
          <option value="all">Все клиенты</option>
          <option value="blocked">Только заблокированные</option>
          <option value="verified">Прошедшие MyID</option>
        </Select>
      </div>

      <Card>
        {list.isLoading ? (
          <p className="text-sm text-neutral-500">Загрузка…</p>
        ) : !list.data || list.data.items.length === 0 ? (
          <p className="text-sm text-neutral-500">Никого не нашлось</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-neutral-500">
                <tr>
                  <th className="pb-2 font-medium">Клиент</th>
                  <th className="pb-2 font-medium">Телефон</th>
                  <th className="pb-2 font-medium">Заказов</th>
                  <th className="pb-2 font-medium">Потрачено</th>
                  <th className="pb-2 font-medium">Статус</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((customer) => (
                  <tr
                    key={customer.id}
                    className="border-t border-neutral-100 dark:border-neutral-800"
                  >
                    <td className="py-2">
                      <button
                        onClick={() => setOpenId(customer.id)}
                        className="text-left hover:underline"
                      >
                        <span className="block">{fullName(customer)}</span>
                        <span className="block text-xs text-neutral-400">
                          {customer.username
                            ? `@${customer.username}`
                            : `id ${customer.telegramId}`}
                        </span>
                      </button>
                    </td>
                    <td className="py-2">{customer.phone ?? '—'}</td>
                    <td className="py-2">
                      {customer.ordersCount}
                      {customer.openOrders > 0 && (
                        <span className="ml-1 text-xs text-neutral-500">
                          ({customer.openOrders} в работе)
                        </span>
                      )}
                    </td>
                    <td className="py-2">{formatSum(customer.totalSpent)}</td>
                    <td className="space-x-1 py-2">
                      {customer.isBlocked && <Badge tone="red">заблокирован</Badge>}
                      {customer.myidVerified && <Badge tone="green">MyID</Badge>}
                      {!customer.phone && <Badge tone="amber">без телефона</Badge>}
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <Button variant="ghost" onClick={() => setOpenId(customer.id)}>
                        Открыть
                      </Button>
                      <Button
                        variant="ghost"
                        className={customer.isBlocked ? '' : 'text-red-600'}
                        onClick={() =>
                          customer.isBlocked
                            ? block.mutate({ id: customer.id, blocked: false })
                            : setBlocking(customer)
                        }
                      >
                        {customer.isBlocked ? 'Разблок.' : 'Заблок.'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {list.data && (
        <Pagination
          page={page}
          pages={list.data.pages}
          total={list.data.total}
          limit={limit}
          onPage={setPage}
          onLimit={(next) => {
            setLimit(next);
            setPage(1);
          }}
          sizes={[20, 50, 100]}
        />
      )}

      {/* ─── Карточка клиента ─── */}
      <Modal
        open={Boolean(openId)}
        title={detail.data ? fullName(detail.data) : 'Клиент'}
        onClose={() => setOpenId(null)}
      >
        {detail.isLoading || !detail.data ? (
          <p className="text-sm text-neutral-500">Загрузка…</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info label="Телефон" value={detail.data.phone ?? '—'} />
              <Info
                label="Telegram"
                value={
                  detail.data.username
                    ? `@${detail.data.username}`
                    : (detail.data.telegramId ?? '—')
                }
              />
              <Info label="Язык" value={detail.data.locale} />
              <Info label="MyID" value={detail.data.myidVerified ? 'пройдена' : 'не пройдена'} />
              <Info
                label="Зарегистрирован"
                value={new Date(detail.data.createdAt).toLocaleDateString('ru-RU')}
              />
              <Info
                label="Последний вход"
                value={
                  detail.data.lastLoginAt
                    ? new Date(detail.data.lastLoginAt).toLocaleDateString('ru-RU')
                    : 'ни разу'
                }
              />
            </div>

            {detail.data.isBlocked && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-950/40 dark:text-red-400">
                Заблокирован{detail.data.blockedReason ? `: ${detail.data.blockedReason}` : ''}
              </p>
            )}

            <div className="grid grid-cols-4 gap-2 rounded-lg bg-neutral-100 p-3 text-center text-sm dark:bg-neutral-800">
              <Stat label="Заказов" value={detail.data.stats.ordersCount} />
              <Stat label="В работе" value={detail.data.stats.openOrders} />
              <Stat label="Завершено" value={detail.data.stats.completed} />
              <Stat label="Отменено" value={detail.data.stats.cancelled} />
            </div>

            <p className="text-sm">
              Потрачено всего:{' '}
              <span className="font-semibold">{formatSum(detail.data.stats.totalSpent)}</span>
            </p>

            {detail.data.addresses.length > 0 && (
              <div>
                <p className="mb-1 text-xs uppercase text-neutral-400">Адреса</p>
                <ul className="space-y-1 text-sm">
                  {detail.data.addresses.map((address) => (
                    <li key={address.id} className="text-neutral-600 dark:text-neutral-400">
                      {address.label && <span className="font-medium">{address.label}: </span>}
                      {address.addressLine}
                      {address.distanceKm !== null && (
                        <span className="text-xs text-neutral-400"> · {address.distanceKm} км</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {detail.data.orders.length > 0 && (
              <div>
                <p className="mb-1 text-xs uppercase text-neutral-400">Заказы</p>
                <div className="max-h-56 space-y-1 overflow-auto text-sm">
                  {detail.data.orders.map((order) => (
                    <Link
                      key={order.id}
                      to={`/orders?order=${order.id}`}
                      onClick={() => setOpenId(null)}
                      className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    >
                      <span className="font-mono text-xs">{order.number}</span>
                      <span className="text-xs text-neutral-500">{order.status}</span>
                      <span className="font-medium">{formatSum(order.total)}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ─── Причина блокировки ─── */}
      <Modal
        open={Boolean(blocking)}
        title="Заблокировать клиента"
        onClose={() => setBlocking(null)}
      >
        {blocking && (
          <div className="space-y-4">
            <p className="text-sm text-neutral-500">
              {fullName(blocking)} потеряет доступ к приложению и не сможет оформлять заказы.
              Действующие сессии закроются сразу.
            </p>

            <Field label="Причина" hint="Останется в карточке и журнале действий">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setBlocking(null)}>
                Отмена
              </Button>
              <Button
                variant="danger"
                disabled={block.isPending}
                onClick={() => block.mutate({ id: blocking.id, blocked: true, reason })}
              >
                Заблокировать
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-neutral-400">{label}</p>
      <p>{value}</p>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <p className="text-lg font-semibold">{value}</p>
      <p className="text-xs text-neutral-500">{label}</p>
    </div>
  );
}
